// PhysicsSimulation.js
// Interactive physics simulations for FlowTutor

import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, RotateCcw, Settings } from 'lucide-react';

const PhysicsSimulation = ({ type, darkMode, onInteraction }) => {
  // Simulation state
  const [isPlaying, setIsPlaying] = useState(false);
  const [angle, setAngle] = useState(30);
  const [mass, setMass] = useState(5);
  const [friction, setFriction] = useState(0.3);
  const [time, setTime] = useState(0);
  const [pendulumAngle, setPendulumAngle] = useState(30);
  const [pendulumVelocity, setPendulumVelocity] = useState(0);
  const [projectileX, setProjectileX] = useState(0);
  const [projectileY, setProjectileY] = useState(0);
  const [velocity, setVelocity] = useState(20);
  const [launchAngle, setLaunchAngle] = useState(45);
  const [ball1X, setBall1X] = useState(200);
  const [ball2X, setBall2X] = useState(600);
  const [ball1Velocity, setBall1Velocity] = useState(5);
  const [ball2Velocity, setBall2Velocity] = useState(0);

  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Reset simulation
  const resetSimulation = () => {
    setIsPlaying(false);
    setTime(0);
    setPendulumAngle(angle);
    setPendulumVelocity(0);
    setProjectileX(0);
    setProjectileY(0);
    setBall1X(200);
    setBall2X(600);
    setBall1Velocity(5);
    setBall2Velocity(0);
    if (onInteraction) {
      onInteraction({ action: 'reset', type });
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    const newIsPlaying = !isPlaying;
    setIsPlaying(newIsPlaying);
    if (onInteraction) {
      onInteraction({ action: newIsPlaying ? 'play' : 'pause', type });
    }
  };

  // Draw inclined plane simulation
  const drawInclinedPlane = (ctx, canvas) => {
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = darkMode ? '#1f2937' : '#f3f4f6';
    ctx.fillRect(0, 0, width, height);

    // Calculate positions
    const planeLength = 400;
    const centerX = width / 2;
    const centerY = height / 2 + 50;
    const angleRad = (angle * Math.PI) / 180;

    // Draw inclined plane
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(-angleRad);
    ctx.fillStyle = darkMode ? '#4b5563' : '#9ca3af';
    ctx.fillRect(-planeLength / 2, -10, planeLength, 20);
    ctx.restore();

    // Draw block
    const blockSize = 50;
    const blockX = centerX - (planeLength / 4) * Math.cos(angleRad);
    const blockY = centerY - (planeLength / 4) * Math.sin(angleRad);

    ctx.save();
    ctx.translate(blockX, blockY);
    ctx.rotate(-angleRad);
    ctx.fillStyle = darkMode ? '#3b82f6' : '#2563eb';
    ctx.fillRect(-blockSize / 2, -blockSize / 2, blockSize, blockSize);
    ctx.restore();

    // Calculate forces
    const g = 9.8;
    const weight = mass * g;
    const normalForce = weight * Math.cos(angleRad);
    const parallelForce = weight * Math.sin(angleRad);
    const frictionForce = friction * normalForce;
    const netForce = parallelForce - frictionForce;

    // Draw force vectors
    const scale = 5;

    // Weight vector (downward)
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(blockX, blockY);
    ctx.lineTo(blockX, blockY + weight * scale);
    ctx.stroke();
    drawArrowhead(ctx, blockX, blockY, blockX, blockY + weight * scale, '#ef4444');

    // Normal force vector (perpendicular to plane)
    const normalX = blockX + normalForce * scale * Math.sin(angleRad);
    const normalY = blockY - normalForce * scale * Math.cos(angleRad);
    ctx.strokeStyle = '#22c55e';
    ctx.beginPath();
    ctx.moveTo(blockX, blockY);
    ctx.lineTo(normalX, normalY);
    ctx.stroke();
    drawArrowhead(ctx, blockX, blockY, normalX, normalY, '#22c55e');

    // Friction force vector (along plane, opposing motion)
    const frictionX = blockX - frictionForce * scale * Math.cos(angleRad);
    const frictionY = blockY - frictionForce * scale * Math.sin(angleRad);
    ctx.strokeStyle = '#f59e0b';
    ctx.beginPath();
    ctx.moveTo(blockX, blockY);
    ctx.lineTo(frictionX, frictionY);
    ctx.stroke();
    drawArrowhead(ctx, blockX, blockY, frictionX, frictionY, '#f59e0b');

    // Draw labels
    ctx.font = '14px system-ui';
    ctx.fillStyle = darkMode ? '#e5e7eb' : '#1f2937';
    ctx.fillText(`Weight: ${weight.toFixed(1)} N`, 20, 30);
    ctx.fillText(`Normal: ${normalForce.toFixed(1)} N`, 20, 50);
    ctx.fillText(`Friction: ${frictionForce.toFixed(1)} N`, 20, 70);
    ctx.fillText(`Net Force: ${netForce.toFixed(1)} N`, 20, 90);
  };

  // Draw pendulum simulation
  const drawPendulum = (ctx, canvas) => {
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = darkMode ? '#1f2937' : '#f3f4f6';
    ctx.fillRect(0, 0, width, height);

    const pivotX = width / 2;
    const pivotY = 100;
    const length = 200;
    const angleRad = (pendulumAngle * Math.PI) / 180;

    // Draw string
    const bobX = pivotX + length * Math.sin(angleRad);
    const bobY = pivotY + length * Math.cos(angleRad);

    ctx.strokeStyle = darkMode ? '#9ca3af' : '#4b5563';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(pivotX, pivotY);
    ctx.lineTo(bobX, bobY);
    ctx.stroke();

    // Draw pivot
    ctx.fillStyle = darkMode ? '#6b7280' : '#374151';
    ctx.beginPath();
    ctx.arc(pivotX, pivotY, 8, 0, 2 * Math.PI);
    ctx.fill();

    // Draw bob
    ctx.fillStyle = '#3b82f6';
    ctx.beginPath();
    ctx.arc(bobX, bobY, 20, 0, 2 * Math.PI);
    ctx.fill();

    // Calculate and display energy
    const g = 9.8;
    const height_bob = length - length * Math.cos(angleRad);
    const PE = mass * g * height_bob;
    const KE = 0.5 * mass * length * length * pendulumVelocity * pendulumVelocity;
    const totalE = PE + KE;

    ctx.font = '14px system-ui';
    ctx.fillStyle = darkMode ? '#e5e7eb' : '#1f2937';
    ctx.fillText(`PE: ${PE.toFixed(1)} J`, 20, 30);
    ctx.fillText(`KE: ${KE.toFixed(1)} J`, 20, 50);
    ctx.fillText(`Total E: ${totalE.toFixed(1)} J`, 20, 70);
  };

  // Draw projectile motion simulation
  const drawProjectile = (ctx, canvas) => {
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = darkMode ? '#1f2937' : '#f3f4f6';
    ctx.fillRect(0, 0, width, height);

    // Draw ground
    ctx.fillStyle = darkMode ? '#374151' : '#9ca3af';
    ctx.fillRect(0, height - 20, width, 20);

    // Draw projectile
    const scale = 2;
    const displayX = 50 + projectileX * scale;
    const displayY = height - 20 - projectileY * scale;

    ctx.fillStyle = '#3b82f6';
    ctx.beginPath();
    ctx.arc(displayX, displayY, 10, 0, 2 * Math.PI);
    ctx.fill();

    // Draw trajectory path
    ctx.strokeStyle = darkMode ? '#60a5fa' : '#3b82f6';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    const angleRad = (launchAngle * Math.PI) / 180;
    const vx = velocity * Math.cos(angleRad);
    const vy = velocity * Math.sin(angleRad);
    const g = 9.8;

    for (let t = 0; t <= 10; t += 0.1) {
      const x = vx * t;
      const y = vy * t - 0.5 * g * t * t;
      if (y >= 0) {
        const px = 50 + x * scale;
        const py = height - 20 - y * scale;
        if (t === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
    }
    ctx.stroke();
    ctx.setLineDash([]);

    // Display info
    ctx.font = '14px system-ui';
    ctx.fillStyle = darkMode ? '#e5e7eb' : '#1f2937';
    ctx.fillText(`X: ${projectileX.toFixed(1)} m`, 20, 30);
    ctx.fillText(`Y: ${projectileY.toFixed(1)} m`, 20, 50);
    ctx.fillText(`Time: ${time.toFixed(2)} s`, 20, 70);
  };

  // Draw collision simulation
  const drawCollision = (ctx, canvas) => {
    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = darkMode ? '#1f2937' : '#f3f4f6';
    ctx.fillRect(0, 0, width, height);

    const centerY = height / 2;

    // Draw track
    ctx.strokeStyle = darkMode ? '#6b7280' : '#9ca3af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, centerY + 30);
    ctx.lineTo(width, centerY + 30);
    ctx.stroke();

    // Draw ball 1 (moving)
    ctx.fillStyle = '#3b82f6';
    ctx.beginPath();
    ctx.arc(ball1X, centerY, 20, 0, 2 * Math.PI);
    ctx.fill();

    // Draw ball 2 (stationary)
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.arc(ball2X, centerY, 20, 0, 2 * Math.PI);
    ctx.fill();

    // Display velocities
    ctx.font = '14px system-ui';
    ctx.fillStyle = darkMode ? '#e5e7eb' : '#1f2937';
    ctx.fillText(`Ball 1: ${ball1Velocity.toFixed(2)} m/s`, 20, 30);
    ctx.fillText(`Ball 2: ${ball2Velocity.toFixed(2)} m/s`, 20, 50);
    ctx.fillText(`Total Momentum: ${(mass * (ball1Velocity + ball2Velocity)).toFixed(2)} kg·m/s`, 20, 70);
  };

  // Helper function to draw arrowheads
  const drawArrowhead = (ctx, fromX, fromY, toX, toY, color) => {
    const headlen = 10;
    const dx = toX - fromX;
    const dy = toY - fromY;
    const angle = Math.atan2(dy, dx);

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  };

  // Animation loop
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const animate = () => {
      if (type === 'incline') {
        drawInclinedPlane(ctx, canvas);
      } else if (type === 'pendulum') {
        drawPendulum(ctx, canvas);
        if (isPlaying) {
          const g = 9.8;
          const length = 200;
          const dt = 0.05;
          const angleRad = (pendulumAngle * Math.PI) / 180;
          const acceleration = -(g / length) * Math.sin(angleRad);
          const newVelocity = pendulumVelocity + acceleration * dt;
          const newAngle = pendulumAngle + (newVelocity * 180) / Math.PI * dt;
          setPendulumVelocity(newVelocity);
          setPendulumAngle(newAngle);
          setTime(t => t + dt);
        }
      } else if (type === 'projectile') {
        drawProjectile(ctx, canvas);
        if (isPlaying) {
          const dt = 0.05;
          const g = 9.8;
          const angleRad = (launchAngle * Math.PI) / 180;
          const vx = velocity * Math.cos(angleRad);
          const vy = velocity * Math.sin(angleRad);
          const newTime = time + dt;
          const newX = vx * newTime;
          const newY = vy * newTime - 0.5 * g * newTime * newTime;
          if (newY >= 0) {
            setProjectileX(newX);
            setProjectileY(newY);
            setTime(newTime);
          } else {
            setIsPlaying(false);
          }
        }
      } else if (type === 'collision') {
        drawCollision(ctx, canvas);
        if (isPlaying) {
          const newBall1X = ball1X + ball1Velocity;
          const newBall2X = ball2X + ball2Velocity;

          // Check for collision
          if (Math.abs(newBall1X - newBall2X) <= 40 && ball1Velocity > 0) {
            // Elastic collision - swap velocities for equal masses
            setBall1Velocity(ball2Velocity);
            setBall2Velocity(ball1Velocity);
          } else {
            setBall1X(newBall1X);
            setBall2X(newBall2X);
          }

          setTime(t => t + 0.05);
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [type, darkMode]); // Only re-create animation loop when type or darkMode changes
  // Note: isPlaying and other state variables are accessed via closure, no need in deps

  // Render controls based on simulation type
  const renderControls = () => {
    if (type === 'incline') {
      return (
        <>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Angle: {angle}°
            </label>
            <input
              type="range"
              min="0"
              max="60"
              value={angle}
              onChange={(e) => {
                setAngle(Number(e.target.value));
                if (onInteraction) onInteraction({ type: 'angle_change', value: Number(e.target.value) });
              }}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-blue-500 to-purple-500"
              style={{ WebkitAppearance: 'none', outline: 'none' }}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Mass: {mass} kg
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={mass}
              onChange={(e) => {
                setMass(Number(e.target.value));
                if (onInteraction) onInteraction({ type: 'mass_change', value: Number(e.target.value) });
              }}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-green-500 to-blue-500"
              style={{ WebkitAppearance: 'none', outline: 'none' }}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Friction: {friction.toFixed(2)}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={friction}
              onChange={(e) => {
                setFriction(Number(e.target.value));
                if (onInteraction) onInteraction({ type: 'friction_change', value: Number(e.target.value) });
              }}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-yellow-500 to-red-500"
              style={{ WebkitAppearance: 'none', outline: 'none' }}
            />
          </div>
        </>
      );
    } else if (type === 'pendulum') {
      return (
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Initial Angle: {angle}°
          </label>
          <input
            type="range"
            min="5"
            max="85"
            value={angle}
            onChange={(e) => {
              const newAngle = Number(e.target.value);
              setAngle(newAngle);
              if (!isPlaying) setPendulumAngle(newAngle);
              if (onInteraction) onInteraction({ type: 'angle_change', value: newAngle });
            }}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-purple-500 to-pink-500"
            style={{ WebkitAppearance: 'none', outline: 'none' }}
            disabled={isPlaying}
          />
        </div>
      );
    } else if (type === 'projectile') {
      return (
        <>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Velocity: {velocity} m/s
            </label>
            <input
              type="range"
              min="5"
              max="40"
              value={velocity}
              onChange={(e) => {
                setVelocity(Number(e.target.value));
                if (onInteraction) onInteraction({ type: 'velocity_change', value: Number(e.target.value) });
              }}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-green-500 to-blue-500"
              style={{ WebkitAppearance: 'none', outline: 'none' }}
              disabled={isPlaying}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Launch Angle: {launchAngle}°
            </label>
            <input
              type="range"
              min="10"
              max="80"
              value={launchAngle}
              onChange={(e) => {
                setLaunchAngle(Number(e.target.value));
                if (onInteraction) onInteraction({ type: 'launch_angle_change', value: Number(e.target.value) });
              }}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-orange-500 to-red-500"
              style={{ WebkitAppearance: 'none', outline: 'none' }}
              disabled={isPlaying}
            />
          </div>
        </>
      );
    } else if (type === 'collision') {
      return (
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Ball 1 Velocity: {ball1Velocity.toFixed(1)} m/s
          </label>
          <input
            type="range"
            min="1"
            max="10"
            step="0.5"
            value={ball1Velocity}
            onChange={(e) => {
              setBall1Velocity(Number(e.target.value));
              if (onInteraction) onInteraction({ type: 'velocity_change', value: Number(e.target.value) });
            }}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-blue-500 to-cyan-500"
            style={{ WebkitAppearance: 'none', outline: 'none' }}
            disabled={isPlaying}
          />
        </div>
      );
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'incline': return 'Inclined Plane with Friction';
      case 'pendulum': return 'Simple Pendulum';
      case 'projectile': return 'Projectile Motion';
      case 'collision': return 'Elastic Collision';
      default: return 'Physics Simulation';
    }
  };

  return (
    <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            {getTitle()}
          </h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={togglePlay}
            className={`p-2 rounded-lg ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={resetSimulation}
            className={`p-2 rounded-lg ${darkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'} text-white transition-colors`}
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={800}
        height={400}
        className="w-full rounded-xl mb-4"
        style={{ maxWidth: '100%', height: 'auto' }}
      />

      {/* Controls */}
      <div className="space-y-4">
        {renderControls()}
      </div>

      {/* Info box */}
      <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
          💡 Simulation Info
        </h4>
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {type === 'incline' && 'Adjust the angle, mass, and friction to see how forces change on an inclined plane.'}
          {type === 'pendulum' && 'Observe energy conservation as the pendulum swings. Total energy remains constant!'}
          {type === 'projectile' && 'Explore parabolic motion by changing velocity and launch angle.'}
          {type === 'collision' && 'Watch momentum conservation in an elastic collision between two balls.'}
        </p>
      </div>
    </div>
  );
};

export default PhysicsSimulation;
